/**
 * SAVE SCHEMA (versioned, migratable).
 * Bump SAVE_VERSION and add a migration step in MIGRATIONS whenever the shape
 * changes. Never remove old migration steps.
 *
 * `settings.musicVolume`/`settings.sfxVolume` replaced the old single
 * `settings.volume` slider (see `src/audio/audio.ts`'s separate music/SFX
 * gain busses). This did NOT need a SAVE_VERSION bump: `migrate()` already
 * runs every loaded save through `deepMergeDefaults()`, which fills in any
 * field missing from an old save with its default, so an old save simply
 * gets `musicVolume: 0.5, sfxVolume: 0.7` the first time it's loaded.
 */
import { defaultBindings, sanitizeBindings, type Bindings } from '../core/bindings';
import { CHARACTERS, DEFAULT_CHARACTER, characterId, type CharacterId } from '../content/characters';
import { META_UPGRADES } from '../content/meta';
import { PET_ASSIGNMENT_STATIONS, PET_BY_ID, type PetAssignmentStation } from '../content/pets';
import { emptyCharacterProgress, validAllocated } from '../content/skills';
import { metaCost } from '../sim/formulas';
import type { Locale } from '../ui/i18n';
import { HOUSE_MODULE_BY_ID, sanitizeHousePlacements, type HousePlacement } from '../house/modules';

export const SAVE_VERSION = 3;
export type ColorblindMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
export type ControlsHudMode = 'always' | 'firstRuns' | 'never';

export interface SettingsData {
  locale: Locale;
  quality: 'low' | 'med' | 'high';
  musicVolume: number;
  sfxVolume: number;
  screenShake: boolean;
  showDamageNumbers: boolean;
  cameraDistance: number;
  highContrastProjectiles: boolean;
  reducedFlash: boolean;
  colorblindMode: ColorblindMode;
  /** Compact in-run input guide: always, only for the first three completed runs, or hidden. */
  controlsHud: ControlsHudMode;
  /** Remappable keyboard/gamepad controls (src/core/bindings.ts). */
  bindings: Bindings;
}

export interface SaveData {
  version: number;
  createdAt: number;
  updatedAt: number;
  chronoshards: number;
  /** Meta upgrade id → tier purchased (0 = none). */
  metaUpgrades: Record<string, number>;
  /** Unlock ids purchased. */
  unlocks: string[];
  /** Item ids owned. */
  inventory: string[];
  equipment: { weapon: string; armor: string | null; relic: string | null };
  /** Era indices whose boss has been beaten (Temporal Anchors). */
  anchors: number[];
  /** Highest paradox level unlocked. */
  paradoxUnlocked: number;
  /** Last non-daily run configuration selected on the main menu. */
  runSetup: { startEra: number; paradox: number; players: number };
  /** Playable characters earned through boss progression. */
  unlockedCharacters: CharacterId[];
  /** Character used by player one; co-op rotates through the unlocked roster. */
  selectedCharacter: string;
  characters: Record<CharacterId, { xp: number; allocated: Record<string, number> }>;
  settings: SettingsData;
  stats: {
    runs: number;
    wins: number;
    bestScore: number;
    bestEra: number;
    fastestClearSec: number | null;
    totalKills: number;
    totalBosses: number;
    noHitStages: number;
    totalShards: number;
    playtimeSec: number;
  };
  /** Persistent mastery totals earned through authored objectives and full Paradox runs. */
  mastery: {
    objectiveClears: number;
    perfectDefenses: number;
    fastHunts: number;
    flawlessEscorts: number;
    noHitBosses: number;
    paradoxWins: number[];
  };
  /** Reserved: pets (see docs/ROADMAP.md). */
  pets: { owned: string[]; active: string | null; levels: Record<string, number>; xp: Record<string, number>; assignments: Record<PetAssignmentStation, string | null> };
  /** Player-owned Observatory upgrades, rewards and editable layout. */
  house: { tier: number; rooms: Record<string, number>; cosmetics: string[]; modules: Record<string, number>; placements: HousePlacement[]; layoutRevision: number };
  /** Daily seeded run tracking: date key → best score. */
  daily: Record<string, number>;
  achievements: string[];
  achievementProgress: Record<string, number>;
  /** Named deterministic challenge id → best eras cleared. */
  challenges: Record<string, number>;
}

export function defaultSave(): SaveData {
  const now = Date.now();
  return {
    version: SAVE_VERSION,
    createdAt: now,
    updatedAt: now,
    chronoshards: 0,
    metaUpgrades: {},
    unlocks: [],
    inventory: ['w_spore_launcher'],
    equipment: { weapon: 'w_spore_launcher', armor: null, relic: null },
    anchors: [],
    paradoxUnlocked: 0,
    runSetup: { startEra: 0, paradox: 0, players: 1 },
    unlockedCharacters: [DEFAULT_CHARACTER],
    selectedCharacter: DEFAULT_CHARACTER,
    characters: emptyCharacterProgress(),
    settings: { locale: 'en', quality: 'med', musicVolume: 0.5, sfxVolume: 0.7, screenShake: true, showDamageNumbers: true, cameraDistance: 30, highContrastProjectiles: false, reducedFlash: false, colorblindMode: 'none', controlsHud: 'always', bindings: defaultBindings() },
    stats: { runs: 0, wins: 0, bestScore: 0, bestEra: 0, fastestClearSec: null, totalKills: 0, totalBosses: 0, noHitStages: 0, totalShards: 0, playtimeSec: 0 },
    mastery: { objectiveClears: 0, perfectDefenses: 0, fastHunts: 0, flawlessEscorts: 0, noHitBosses: 0, paradoxWins: [] },
    pets: { owned: [], active: null, levels: {}, xp: {}, assignments: { chronolab: null, garden: null, wardrobe: null } },
    house: { tier: 0, rooms: {}, cosmetics: [], modules: {}, placements: [], layoutRevision: 0 },
    daily: {},
    achievements: [],
    achievementProgress: {},
    challenges: {},
  };
}

type Migration = (data: Record<string, unknown>) => Record<string, unknown>;

/** MIGRATIONS[n] upgrades a save from version n to n+1. */
export const MIGRATIONS: Record<number, Migration> = {
  // 0 → 1: pre-release saves had no version; seed all fields from defaults
  0: (d) => ({ ...(defaultSave() as unknown as Record<string, unknown>), ...d, version: 1 }),
  1: (d) => {
    const upgrades = d.metaUpgrades && typeof d.metaUpgrades === 'object' ? d.metaUpgrades as Record<string, number> : {};
    let refund = 0;
    for (const def of META_UPGRADES) for (let tier = 0; tier < Math.max(0, Math.min(def.tiers, Math.floor(upgrades[def.id] ?? 0))); tier++) refund += metaCost(def.baseCost, def.costGrowth, tier);
    const stats = d.stats && typeof d.stats === 'object' ? d.stats as Record<string, unknown> : {};
    const runs = typeof stats.runs === 'number' ? stats.runs : 0;
    const unlocked = Array.isArray(d.unlockedCharacters) ? d.unlockedCharacters.map(characterId) : [DEFAULT_CHARACTER];
    const characters = emptyCharacterProgress();
    const veteranLevel = Math.min(10, 1 + Math.floor(runs / 4));
    const veteranXp = cumulativeCharacterXp(veteranLevel);
    for (const id of unlocked) characters[id].xp = veteranXp;
    const pets = d.pets && typeof d.pets === 'object' ? d.pets as Record<string, unknown> : {};
    return { ...d, version: 2, chronoshards: Math.max(0, Number(d.chronoshards) || 0) + refund, metaUpgrades: {}, characters, pets: { ...pets, xp: {} } };
  },
  // 2 → 3: Observatory construction inventory and editable layout.
  2: (d) => {
    const house = d.house && typeof d.house === 'object' ? d.house as Record<string, unknown> : {};
    return { ...d, version: 3, house: { ...house, modules: {}, placements: [], layoutRevision: 0 } };
  },
};

function cumulativeCharacterXp(level: number): number {
  let xp = 0;
  for (let l = 1; l < level; l++) xp += Math.round(180 + 20 * l ** 1.45);
  return xp;
}

function skillPointBudget(xp: number): number {
  let level = 1;
  let remaining = Math.max(0, xp);
  while (level < 30) {
    const next = Math.round(180 + 20 * level ** 1.45);
    if (remaining < next) break;
    remaining -= next;
    level++;
  }
  return Math.max(0, level - 1) + (level >= 10 ? 1 : 0) + (level >= 20 ? 1 : 0) + (level >= 30 ? 1 : 0);
}

/** Migrate then fill any missing fields from defaults (defensive against partial saves). */
export function migrate(raw: unknown): SaveData {
  let d = (raw && typeof raw === 'object' ? { ...(raw as Record<string, unknown>) } : {}) as Record<string, unknown>;
  let v = typeof d.version === 'number' ? d.version : 0;
  while (v < SAVE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) throw new Error(`No migration from save version ${v}`);
    d = step(d);
    v = typeof d.version === 'number' ? d.version : v + 1;
  }
  const def = defaultSave();
  const out = deepMergeDefaults(def as unknown as Record<string, unknown>, d) as unknown as SaveData;
  out.version = SAVE_VERSION;
  out.unlockedCharacters = [...new Set(out.unlockedCharacters.map(characterId))];
  if (!out.unlockedCharacters.includes(DEFAULT_CHARACTER)) out.unlockedCharacters.unshift(DEFAULT_CHARACTER);
  for (const character of CHARACTERS) {
    if (character.unlockAfterEra >= 0 && out.anchors.includes(character.unlockAfterEra) && !out.unlockedCharacters.includes(character.id)) out.unlockedCharacters.push(character.id);
  }
  out.selectedCharacter = characterId(out.selectedCharacter);
  if (!out.unlockedCharacters.includes(out.selectedCharacter as CharacterId)) out.selectedCharacter = DEFAULT_CHARACTER;
  out.settings.cameraDistance = Math.min(44, Math.max(26, Number.isFinite(out.settings.cameraDistance) ? out.settings.cameraDistance : 30));
  if (!['en', 'es'].includes(out.settings.locale)) out.settings.locale = 'en';
  if (!['none', 'protanopia', 'deuteranopia', 'tritanopia'].includes(out.settings.colorblindMode)) out.settings.colorblindMode = 'none';
  if (!['always', 'firstRuns', 'never'].includes(out.settings.controlsHud)) out.settings.controlsHud = 'always';
  out.settings.bindings = sanitizeBindings(out.settings.bindings);
  for (const character of CHARACTERS) {
    const progress = out.characters[character.id] ?? { xp: 0, allocated: {} };
    progress.xp = Math.max(0, Number(progress.xp) || 0);
    progress.allocated = validAllocated(character.id, progress.allocated, skillPointBudget(progress.xp));
    out.characters[character.id] = progress;
  }
  for (const id of Object.keys(out.pets.xp)) out.pets.xp[id] = Math.max(0, Number(out.pets.xp[id]) || 0);
  out.house.placements = sanitizeHousePlacements(out.house.placements);
  for (const id of Object.keys(out.house.modules)) {
    if (!HOUSE_MODULE_BY_ID.has(id)) delete out.house.modules[id];
    else out.house.modules[id] = Math.max(0, Math.min(99, Math.floor(Number(out.house.modules[id]) || 0)));
  }
  // A synced/legacy layout can never contain more copies than the owned inventory.
  for (const placement of out.house.placements) out.house.modules[placement.moduleId] = Math.max(out.house.modules[placement.moduleId] ?? 0, out.house.placements.filter((p) => p.moduleId === placement.moduleId).length);
  out.house.layoutRevision = Math.max(0, Math.floor(Number(out.house.layoutRevision) || 0));
  const assigned = new Set<string>();
  for (const station of PET_ASSIGNMENT_STATIONS) {
    const id = out.pets.assignments[station.id];
    if (!id || !PET_BY_ID.has(id) || !out.pets.owned.includes(id) || id === out.pets.active || assigned.has(id)) out.pets.assignments[station.id] = null;
    else assigned.add(id);
  }
  return out;
}

function deepMergeDefaults(def: Record<string, unknown>, src: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...def };
  for (const k of Object.keys(src)) {
    const dv = def[k];
    const sv = src[k];
    if (dv && typeof dv === 'object' && !Array.isArray(dv) && sv && typeof sv === 'object' && !Array.isArray(sv)) {
      out[k] = deepMergeDefaults(dv as Record<string, unknown>, sv as Record<string, unknown>);
    } else if (sv !== undefined) out[k] = sv;
  }
  return out;
}

const KEY = 'chronofall.save.v1';

export function loadLocal(): SaveData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultSave();
    return migrate(JSON.parse(raw));
  } catch (e) {
    console.warn('save: failed to load, starting fresh', e);
    return defaultSave();
  }
}

export function saveLocal(data: SaveData): void {
  try {
    data.updatedAt = Date.now();
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('save: failed to write', e);
  }
}

export function wipeLocal(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Cloud merge policy: newer `updatedAt` wins, but currency/unlocks are unioned so nothing is lost. */
export function mergeSaves(a: SaveData, b: SaveData): SaveData {
  const newer = a.updatedAt >= b.updatedAt ? a : b;
  const older = newer === a ? b : a;
  const out = structuredClone(newer);
  out.chronoshards = Math.max(a.chronoshards, b.chronoshards);
  out.unlocks = [...new Set([...a.unlocks, ...b.unlocks])];
  out.achievements = [...new Set([...a.achievements, ...b.achievements])];
  for (const id of new Set([...Object.keys(a.achievementProgress), ...Object.keys(b.achievementProgress)])) {
    out.achievementProgress[id] = Math.max(a.achievementProgress[id] ?? 0, b.achievementProgress[id] ?? 0);
  }
  for (const id of new Set([...Object.keys(a.challenges), ...Object.keys(b.challenges)])) out.challenges[id] = Math.max(a.challenges[id] ?? 0, b.challenges[id] ?? 0);
  out.unlockedCharacters = [...new Set([...a.unlockedCharacters, ...b.unlockedCharacters])];
  out.pets.owned = [...new Set([...a.pets.owned, ...b.pets.owned])];
  for (const id of new Set([...Object.keys(a.pets.levels), ...Object.keys(b.pets.levels)])) out.pets.levels[id] = Math.max(a.pets.levels[id] ?? 0, b.pets.levels[id] ?? 0);
  for (const id of new Set([...Object.keys(a.pets.xp), ...Object.keys(b.pets.xp)])) out.pets.xp[id] = Math.max(a.pets.xp[id] ?? 0, b.pets.xp[id] ?? 0);
  const assignedPets = new Set(Object.values(out.pets.assignments).filter((id): id is string => Boolean(id)));
  for (const station of PET_ASSIGNMENT_STATIONS) {
    const candidate = older.pets.assignments[station.id];
    if (!out.pets.assignments[station.id] && candidate && candidate !== out.pets.active && out.pets.owned.includes(candidate) && !assignedPets.has(candidate)) {
      out.pets.assignments[station.id] = candidate;
      assignedPets.add(candidate);
    }
  }
  for (const character of CHARACTERS) {
    out.characters[character.id].xp = Math.max(a.characters[character.id]?.xp ?? 0, b.characters[character.id]?.xp ?? 0);
  }
  out.inventory = [...new Set([...a.inventory, ...b.inventory])];
  out.anchors = [...new Set([...a.anchors, ...b.anchors])].sort((x, y) => x - y);
  for (const key of new Set([...Object.keys(a.daily), ...Object.keys(b.daily)])) out.daily[key] = Math.max(a.daily[key] ?? 0, b.daily[key] ?? 0);
  out.paradoxUnlocked = Math.max(a.paradoxUnlocked, b.paradoxUnlocked);
  for (const k of Object.keys(older.metaUpgrades)) out.metaUpgrades[k] = Math.max(out.metaUpgrades[k] ?? 0, older.metaUpgrades[k]);
  out.stats.runs = Math.max(a.stats.runs, b.stats.runs);
  out.stats.wins = Math.max(a.stats.wins, b.stats.wins);
  out.stats.bestScore = Math.max(a.stats.bestScore, b.stats.bestScore);
  out.stats.bestEra = Math.max(a.stats.bestEra, b.stats.bestEra);
  out.stats.totalKills = Math.max(a.stats.totalKills, b.stats.totalKills);
  out.stats.totalBosses = Math.max(a.stats.totalBosses, b.stats.totalBosses);
  out.stats.noHitStages = Math.max(a.stats.noHitStages, b.stats.noHitStages);
  out.mastery.objectiveClears = Math.max(a.mastery.objectiveClears, b.mastery.objectiveClears);
  out.mastery.perfectDefenses = Math.max(a.mastery.perfectDefenses, b.mastery.perfectDefenses);
  out.mastery.fastHunts = Math.max(a.mastery.fastHunts, b.mastery.fastHunts);
  out.mastery.flawlessEscorts = Math.max(a.mastery.flawlessEscorts, b.mastery.flawlessEscorts);
  out.mastery.noHitBosses = Math.max(a.mastery.noHitBosses, b.mastery.noHitBosses);
  out.mastery.paradoxWins = [...new Set([...a.mastery.paradoxWins, ...b.mastery.paradoxWins])].sort((x, y) => x - y);
  out.house.cosmetics = [...new Set([...a.house.cosmetics, ...b.house.cosmetics])];
  for (const id of new Set([...Object.keys(a.house.modules), ...Object.keys(b.house.modules)])) out.house.modules[id] = Math.max(a.house.modules[id] ?? 0, b.house.modules[id] ?? 0);
  // Layout is an atomic document: newest edit revision wins, with updatedAt as tie-breaker.
  const layoutSource = a.house.layoutRevision === b.house.layoutRevision ? newer : a.house.layoutRevision > b.house.layoutRevision ? a : b;
  out.house.placements = structuredClone(layoutSource.house.placements);
  out.house.layoutRevision = layoutSource.house.layoutRevision;
  return out;
}
